<template>
    <ProgressSpinner class="mt-4" style="width: 50px; height: 50px" v-if="loading" />
    <div
        v-else
        :class="`flex vertical full-height overflow-hidden ${isMaximized ? 'chat-channel p-4' : ''}`"
    >
        <div
            :class="`display-flex align-items-center justify-content-between ${isMaximized ? 'chat-channel-header mb-2' : 'mt-2 mb-2'}`"
        >
            <div class="display-flex align-items-center gap-2">
                <img class="profile-details-avatar-sm" src="@/assets/img/profile.png" alt="" />
                <span class="text-xl font-semibold text-900">{{ patientFullName || 'Unknown' }}</span>
            </div>
            <Button
                v-if="!isAIChannel"
                icon="pi pi-ellipsis-v"
                class="p-button-text p-button-rounded p-button-plain p-button-sm p-1"
                @click="channelMenu_clickHandler"
                :aria-label="'Channel menu for ' + patientFullName"
            />
            <Menu 
                ref="channelMenuRef"
                :model="channelMenuItems"
                :popup="true"
                :appendTo="'body'"
            />
        </div>

        <div v-if="!isAIChannel && primaryPhoneInfo" class="display-flex align-items-center gap-2 mb-2">
            <i class="pi pi-phone text-500 text-xs"></i>
            <span class="text-sm font-medium text-700">{{ primaryPhoneInfo.displayNumber }}</span>
            <span :class="phoneTypeBadgeClass">{{ primaryPhoneInfo.displayType }}</span>
            <span 
                v-if="primaryPhoneInfo.carrierName"
                class="px-2 py-1 border-round text-xs bg-gray-100 text-gray-700"
            >
                {{ primaryPhoneInfo.carrierName }}
            </span>
            <i v-if="primaryPhoneInfo.isValid" class="pi pi-check-circle text-green-500 text-xs" title="Validated"></i>
        </div>

        <div v-if="!isAIChannel" class="display-flex align-items-center gap-2 mb-2 flex-wrap-wrap">
            <span 
                v-if="isManagedByMe"
                class="px-2 py-1 border-round text-xs font-medium bg-green-100 text-green-700"
            >
                You're Managing
            </span>
            <span 
                v-else-if="channelOwnerText !== 'Managed by: No one'"
                class="px-2 py-1 border-round text-xs bg-gray-100 text-gray-600"
            >
                {{ channelOwnerText }}
            </span>
            <span :class="userRoleBadgeClass">{{ userRoleLabel }}</span>
            <span class="px-2 py-1 border-round text-xs bg-blue-50 text-blue-700">
                {{ participantCount }} {{ participantCount === 1 ? 'member' : 'members' }}
            </span>
        </div>

        <InlineMessage
            v-if="fetchError"
            class="flex flex-grow-0 justify-content-start align-items-center mt-3"
            severity="error"
            >{{ fetchError.message || 'Chat service has encountered an error' }}</InlineMessage
        >

        <div class="chat-thread-container">
            <div class="flex flex-column overflow-hidden">
                <div v-if="!isAIChannel" class="display-flex align-items-center gap-2 p-3 mb-2 border-round bg-blue-50 border-1 border-blue-200">
                    <i class="pi pi-info-circle text-blue-600"></i>
                    <span class="text-sm text-blue-900">To safeguard patient privacy, do not use SMS for sharing PHI.</span>
                </div>
                <div class="chat-message-thread">
                    <div class="flex flex-column flex-grow-0" v-for="(item, index) in threadItems" :key="index">
                        <span v-if="item.type === 'separator'" class="start-message">{{ item.text }}</span>
                        <span v-else-if="item.type === 'error'" class="send-error"> {{ item.text }}</span>
                        <span v-else-if="item.type === 'system'" class="system-message">
                            <i class="pi pi-info-circle mr-2"></i>{{ item.text }}
                        </span>
                        <span v-else-if="item.type === 'loading'" class="flex flex-row mr-3 mb-3 align-self-end">
                            <ProgressSpinner style="width: 16px; height: 16px" />
                        </span>
                        <ChatSidebarMessageThreadItem v-else :channel="channel" :message="item" />
                    </div>
                    <span v-if="!hasEarlierMessages && !isAIChannel" class="start-message"
                        >If you think you may be experiencing a life threatening emergency. We recommend you immediately
                        call 911 for medical assistance, or go to a nearby emergency room.</span
                    >
                    <span v-if="!hasEarlierMessages && !isAIChannel" class="start-message"
                        >Please allow 1 business day for our team to get back to you.</span
                    >
                    <span v-if="!hasEarlierMessages && !isAIChannel" class="start-message">Start of Conversation</span>
                    <ProgressSpinner
                        v-if="loadingAdditionalMessages"
                        class="mt-4 align-self-center"
                        style="width: 24px; height: 24px"
                    />
                </div>
                <div class="chat-submission">
                    <Textarea v-model="draftMessage" :autoResize="true" placeholder="Message" rows="1" cols="30" />
                    <SplitButton
                        :class="`send-button p-button p-button-sm pa-0 mt-2`"
                        :label="sendAs.buttonLabel"
                        icon="pi pi-send"
                        @click="message_submitHandler"
                        :model="sendAsOptions"
                        :disabled="sendingMessage"
                        buttonClass="dropdown-button"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { setupScrollDetection } from '@/helpers'
import ChatSidebarMessageThreadItem from '@/components/layout/sidebar/sidebar-panels/chat/ChatSidebarMessageThreadItem'
import Menu from 'primevue/menu'
import Button from 'primevue/button'
import { ref, computed, onMounted, onUnmounted, watch } from '@vue/composition-api'

export default {
    components: {
        ChatSidebarMessageThreadItem,
        Menu,
        Button,
    },
    props: {
        principal: {
            type: String,
            required: true,
        },
        maximized: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { root, emit }) {
        const channelPrincipal = computed(() => props.principal)
        const isMaximized = computed(() => props.maximized)

        const smsMedium = { label: 'SMS', value: 'Sms', buttonLabel: 'Send via SMS' }
        const appMedium = { label: 'App', value: 'InApp', buttonLabel: 'Send via App' }
        const aiMedium = { label: 'AI', value: 'AI', buttonLabel: 'Ask AI' }
        const sendAsDefault = ref(null)
        const sendAs = ref(smsMedium)

        const channel = ref(null)
        const profile = ref(null)
        const validatedPhones = ref([])
        const draftMessage = ref('')
        const loading = ref(true)
        const loadingAdditionalMessages = ref(false)
        const sendingMessage = ref(false)
        const systemMessages = ref([])

        // Store State and Getters
        const channels = computed(() => root.$store.state.messaging.channels)
        const fetchError = computed(() => root.$store.state.messaging.fetchError)
        const sendErrors = computed(() => root.$store.state.messaging.sendErrors)
        const getMessagesByChannelId = computed(() => root.$store.getters['messaging/getMessagesByChannelId'])
        const hasEarlierMessagesByChannelId = computed(
            () => root.$store.getters['messaging/hasEarlierMessagesByChannelId']
        )
        const expanded = computed(() => root.$store.state.sidebar.expanded)
        const userId = computed(() => root.$store.getters['portal/userId'])
        const curSiteHasFeatureValue = computed(() => root.$store.getters['portal/curSiteHasFeatureValue'])
        const hasAnySiteFromListFeatureValue = computed(
            () => root.$store.getters['portal/hasAnySiteFromListFeatureValue']
        )
        const messages = computed(() => getMessagesByChannelId.value(channel.value?.id) || [])

        const sendAsOptions = computed(() => {
            let options = [
                {
                    label: 'SMS',
                    command: () => {
                        sendAs.value = sendAsDefault.value = smsMedium
                    },
                },
            ]

            // If we have a channel, we can use channel tenants to find feature flag
            // If not, we can assume that it is a new chat opened in the context of current site
            const appChatFeatureEnabled =
                channel.value && channel.value.tenants
                    ? hasAnySiteFromListFeatureValue.value(channel.value.tenants, 'enable_in_app_chat', 'true')
                    : curSiteHasFeatureValue.value('enable_in_app_chat', 'true')

            const appChatAvailable = appChatFeatureEnabled && channel.value && channel.value.isAppChatAvailable

            // Default delivery method
            let defaultMedium = sendAsDefault.value || smsMedium

            if (appChatAvailable) {
                options.push({
                    label: 'APP',
                    command: () => {
                        sendAs.value = sendAsDefault.value = appMedium
                    },
                })
            }

            // Do not override default if user
            // has already selected a medium
            if (!sendAsDefault.value) {
                // Override default if app chat is available
                if (appChatAvailable) defaultMedium = appMedium

                // Use last message medium if available
                const myLastMessage = messages.value.find((msg) => msg.isMyMessage)
                if (myLastMessage?.medium === 'Sms') defaultMedium = smsMedium
            }
            if (channel.value?.isAI) {
                defaultMedium = aiMedium
                options = []
            }

            sendAs.value = defaultMedium
            return options
        })

        const threadItems = computed(() => {
            const items = addDateSeparators(messages.value)
            
            // Add system messages at the top
            systemMessages.value.forEach(sysMsg => {
                items.unshift(sysMsg)
            })
            
            if (sendError.value) {
                items.unshift({
                    type: 'error',
                    text: sendError.value,
                })
            }
            if (sendingMessage.value) {
                items.unshift({
                    type: 'loading',
                    text: 'Sending...',
                })
            }
            return items
        })

        const isManagedByMe = computed(() => channel.value?.owner === userId.value)
        const isAIChannel = computed(() => channel.value?.isAI === true)
        const patientFullName = computed(() => {
            return (
                channel.value?.patientFullName ||
                (profile.value ? (profile.value.firstName || '') + ' ' + (profile.value.lastName || '') : '')
            )
        })

        const channelOwnerText = computed(() => {
            if (!channel.value?.owner) {
                return 'Managed by: No one'
            } else if (channel.value?.owner !== userId.value) {
                return `Managed by: ${channel.value?.ownerPii?.firstName || ''} ${channel.value?.ownerPii?.lastName || ''}`
            } else {
                return 'Managed by you'
            }
        })

        const hasEarlierMessages = computed(
            () => channel.value && hasEarlierMessagesByChannelId.value(channel.value.id)
        )
        const sendError = computed(() => sendErrors.value[channelPrincipal.value])

        const primaryPhoneInfo = computed(() => {
            if (!profile.value || !profile.value.phones || !profile.value.phones.length) return null
            
            const primaryPhone = profile.value.phones[0]
            const displayType = primaryPhone.type ? 
                primaryPhone.type.charAt(0) + primaryPhone.type.slice(1).toLowerCase() : 
                'Unknown'
            
            // Build display number - handle different phone number field names
            let displayNumber = 'No number'
            const phoneNumber = primaryPhone.phone_number || primaryPhone.phoneNumber
            const countryCode = primaryPhone.country_code || primaryPhone.countryCode
            
            if (phoneNumber) {
                if (countryCode) {
                    displayNumber = `+${countryCode} ${phoneNumber}`
                } else {
                    displayNumber = phoneNumber
                }
            }
            
            // Find validated phone data - check embedded data first
            let validatedData = null
            
            // First, check if phone has embedded validation data
            if (primaryPhone.validationTime) {
                validatedData = {
                    valid: primaryPhone.validLine,
                    phoneNumberType: primaryPhone.phoneNumberType,
                    carrierName: primaryPhone.carrierName
                }
            } else if (validatedPhones.value.length > 0 && phoneNumber) {
                // Otherwise, try to find it in the API response
                validatedData = validatedPhones.value.find(vp => {
                    const apiPhone = vp.phoneNumber
                    return apiPhone === phoneNumber || 
                           apiPhone === `${countryCode}${phoneNumber}` || 
                           apiPhone === `+${countryCode}${phoneNumber}` ||
                           apiPhone.replace(/^\+/, '') === `${countryCode}${phoneNumber}`
                })
            }
            
            // Use validated phone type if available, otherwise fall back to profile type
            const phoneType = validatedData?.phoneNumberType || primaryPhone.type || 'UNKNOWN'
            const validatedDisplayType = phoneType !== 'UNKNOWN' && phoneType !== 'null' ?
                phoneType.charAt(0) + phoneType.slice(1).toLowerCase() : 
                displayType
            
            return {
                type: phoneType,
                displayType: validatedDisplayType,
                displayNumber,
                carrierName: validatedData?.carrierName && validatedData.carrierName !== 'null' ? 
                    validatedData.carrierName : null,
                isValid: validatedData?.valid
            }
        })

        const userRoleLabel = computed(() => {
            const myParticipant = channel.value?.participantsMap?.get(userId.value)
            if (!myParticipant) return 'Not Participating'
            
            switch (myParticipant.role) {
                case 'CARE_COORDINATOR': return 'Care Coordinator'
                case 'WATCHER': return 'Watching'
                case 'PATIENT': return 'Patient'
                default: return myParticipant.role
            }
        })

        const userRoleSeverity = computed(() => {
            const myParticipant = channel.value?.participantsMap?.get(userId.value)
            if (!myParticipant) return 'secondary'
            
            // Care Coordinator is the primary role (blue)
            // Watcher is secondary role (gray) - only shown if not care coordinator
            // Not participating is also secondary
            switch (myParticipant.role) {
                case 'CARE_COORDINATOR': return 'primary'
                case 'WATCHER': return 'warning'
                case 'PATIENT': return 'info'
                default: return 'secondary'
            }
        })
        
        const userRoleIcon = computed(() => {
            const myParticipant = channel.value?.participantsMap?.get(userId.value)
            if (!myParticipant) return 'pi-times-circle'
            
            switch (myParticipant.role) {
                case 'CARE_COORDINATOR': return 'pi-star-fill'
                case 'WATCHER': return 'pi-eye'
                case 'PATIENT': return 'pi-user'
                default: return 'pi-question-circle'
            }
        })

        const participantCount = computed(() => {
            return channel.value?.participants?.length || 0
        })

        const sendMessage = (payload) => root.$store.dispatch('messaging/sendMessage', payload)
        const watchChannel = (channel) => root.$store.dispatch('messaging/watchChannel', { channel })
        const unwatchChannel = (channel, message) => root.$store.dispatch('messaging/unwatchChannel', { channel, message })
        const unreadMessage = (message) => root.$store.dispatch('messaging/unreadMessage', { message })
        const inviteToChannelByEmail = (channel, email, message) => root.$store.dispatch('messaging/inviteToChannelByEmail', { channel, email, message })
        const fetchMessagesForChannel = (payload) => root.$store.dispatch('messaging/fetchMessagesForChannel', payload)
        const subscribeToChannel = (payload) => root.$store.dispatch('messaging/subscribeToChannel', payload)
        const unsubscribeFromChannel = (payload) => root.$store.dispatch('messaging/unsubscribeFromChannel', payload)

        const loadChannel = async () => {
            if (!channelPrincipal.value) {
                // ? Where is this being caught?
                throw new Error('A principal is required to load chat thread')
            }
            loading.value = true
            channel.value = await subscribeToChannel(channelPrincipal.value)
            if (!channel.value) {
                await loadProfile() // If no channel exists yet for principal, fetch patient info (i.e. name)
            } else {
                // Always load profile data to get phone information for existing channels
                await loadProfile()
            }
            loading.value = false
            setTimeout(() => {
                setupScrollDetection('.chat-message-thread', 'show-scrollbar')
                setupInfiniteScrolling()
            }, 200)
        }

        const setupInfiniteScrolling = () => {
            const scrollContainer = document.querySelector('.chat-message-thread')
            scrollContainer &&
                scrollContainer.addEventListener('scroll', async () => {
                    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
                    if (Math.abs(scrollTop) + 4 > scrollHeight - clientHeight) {
                        if (hasEarlierMessages.value && !loadingAdditionalMessages.value) {
                            loadingAdditionalMessages.value = true
                            await fetchMessagesForChannel({ channelId: channel.value.id, additional: true })
                            loadingAdditionalMessages.value = false
                        }
                    }
                })
        }

        const loadProfile = async () => {
            const response = await root.$apiv2.getProfileById(channelPrincipal.value)
            profile.value = response?.data || null
            
            // Load validated phone data only if needed
            if (profile.value && profile.value.phones) {
                const needsValidation = profile.value.phones.some(phone => !phone.validationTime)
                
                if (needsValidation) {
                    try {
                        validatedPhones.value = await root.$apiv2.validatePhoneNumber(profile.value.id)
                    } catch (error) {
                        console.error('Failed to load validated phone data:', error)
                        validatedPhones.value = []
                    }
                } else {
                    console.log('All phones already validated, using embedded data')
                    validatedPhones.value = []
                }
            }
        }

        const message_submitHandler = async () => {
            if (!draftMessage.value) {
                return
            }

            sendingMessage.value = true

            const text = draftMessage.value
            draftMessage.value = ''

            if (text.startsWith('/') || text.startsWith("\\")) {
                const [rawCmd, ...rawArgs] = text.trim().split(' ');
                const cmd = rawCmd.replace(/^[/\\]+/, '').toLowerCase();
                const args = rawArgs.map(arg => arg.trim()).filter(arg => arg.length > 0);

                switch (cmd) {
                    case 'unread':
                        const lastInboundMessage = messages.value.find((msg) => !msg.isMyMessage);
                        if (lastInboundMessage) {
                            await unreadMessage(lastInboundMessage);
                        }
                        break;
                    case 'watch':
                        if (channel.value.id) {
                            await watchChannel(channel.value);
                        }
                        break;
                    case 'unwatch':
                        if (channel.value.id) {
                            await unwatchChannel(channel.value, args.length > 0 ? args.join(' ') : null);
                        }
                        break;
                    case 'invite':
                        const email = args.shift();
                        const message = args.join(' ');
                        if (email) {
                            await inviteToChannelByEmail(channel.value, email, message);
                        }
                        break;
                    default:
                        root.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Unknown command type: ' + cmd,
                            life: 3000
                        });
                        break;
                }
                sendingMessage.value = false;
                return;
            }

            const sentMessage = await sendMessage({
                medium: sendAs.value.value,
                principal: channelPrincipal.value,
                message: text,
            })

            if (sentMessage) {
                if (!channel.value?.id) {
                    channel.value = { id: sentMessage.channelId }
                }
            } else {
                draftMessage.value = text
            }

            sendingMessage.value = false
        }

        const addDateSeparators = (sortedMessageList) => {
            // Assumes it is provided a list of messages sorted from newest to oldest.
            let startOfNextTimePeriod = null
            return sortedMessageList.reduce((acc, msg, ix) => {
                const msgCreatedDate = moment(msg.createdDate)
                if (shouldStartNewTimePeriod(msgCreatedDate, startOfNextTimePeriod)) {
                    if (startOfNextTimePeriod) {
                        acc.push({
                            type: 'separator',
                            text: startOfNextTimePeriod.calendar(null, {
                                sameElse: 'MM/DD/YYYY [at] LT',
                            }),
                        })
                    }
                    startOfNextTimePeriod = msgCreatedDate
                }
                acc.push(msg)
                if (ix === sortedMessageList.length - 1) {
                    acc.push({
                        type: 'separator',
                        text: msgCreatedDate.calendar(null, {
                            sameElse: 'MM/DD/YYYY [at] LT',
                        }),
                    })
                }
                return acc
            }, [])
        }

        const shouldStartNewTimePeriod = (date1, date2) => {
            return !date1 || !date2 || date2.diff(date1, 'hours') >= 1
        }

        const phoneTypeBadgeClass = computed(() => {
            const type = primaryPhoneInfo.value?.type?.toUpperCase()
            switch (type) {
                case 'CELL':
                    return 'px-2 py-1 border-round text-xs font-medium bg-green-100 text-green-700'
                case 'HOME':
                case 'WORK':
                    return 'px-2 py-1 border-round text-xs font-medium bg-orange-100 text-orange-700'
                case 'UNKNOWN':
                default:
                    return 'px-2 py-1 border-round text-xs bg-gray-100 text-gray-600'
            }
        })

        const userRoleBadgeClass = computed(() => {
            const myParticipant = channel.value?.participantsMap?.get(userId.value)
            if (!myParticipant) return 'px-2 py-1 border-round text-xs bg-gray-100 text-gray-600'
            
            switch (myParticipant.role) {
                case 'CARE_COORDINATOR':
                    return 'px-2 py-1 border-round text-xs font-medium bg-blue-100 text-blue-700'
                case 'WATCHER':
                    return 'px-2 py-1 border-round text-xs font-medium bg-orange-100 text-orange-700'
                case 'PATIENT':
                    return 'px-2 py-1 border-round text-xs bg-blue-50 text-blue-600'
                default:
                    return 'px-2 py-1 border-round text-xs bg-gray-100 text-gray-600'
            }
        })

        // Channel menu functionality
        const isWatching = computed(() => {
            return channel.value?.participants?.some(p => 
                p.principal === userId.value && p.role === 'WATCHER'
            )
        })

        const channelMenuItems = computed(() => {
            if (!channel.value || isAIChannel.value) return []
            
            const myParticipant = channel.value.participantsMap?.get(userId.value)
            const hasUnreadMessages = myParticipant?.unreadMessagesCount > 0
            const isParticipant = !!myParticipant
            const hasNonWatcherRole = isParticipant && myParticipant.role !== 'WATCHER'
            
            const items = []

            // Read/Unread functionality - available for any channel you're a participant in
            if (isParticipant) {
                items.push({
                    label: hasUnreadMessages ? 'Mark as Read' : 'Mark as Unread',
                    icon: hasUnreadMessages ? 'pi pi-check' : 'pi pi-times',
                    command: () => toggleReadStatus()
                })
            }

            // Watch/Unwatch functionality - only show if user is not already assigned with another role
            // If user is already a CARE_COORDINATOR or other role, don't show watch options
            if (!hasNonWatcherRole) {
                if (isWatching.value) {
                    items.push({
                        label: 'Stop Watching',
                        icon: 'pi pi-eye-slash',
                        command: () => unwatchChannelFromThread()
                    })
                } else {
                    items.push({
                        label: 'Watch Channel',
                        icon: 'pi pi-eye',
                        command: () => watchChannelFromThread()
                    })
                }
            }

            // Invite team member - only for channels you own
            if (isManagedByMe.value) {
                items.push({
                    label: 'Invite Team Member',
                    icon: 'pi pi-user-plus',
                    command: () => showInviteDialogFromThread()
                })
            }
            
            return items
        })

        const channelMenuRef = ref(null)

        const channelMenu_clickHandler = (event) => {
            console.log('Channel menu button clicked')
            console.log('channelMenuRef:', channelMenuRef.value)
            console.log('channelMenuItems:', channelMenuItems.value)
            if (channelMenuRef.value) {
                channelMenuRef.value.toggle(event)
            } else {
                console.error('channelMenuRef is null')
            }
        }

        const toggleReadStatus = async () => {
            if (!channel.value?.id) return
            
            const myParticipant = channel.value.participantsMap?.get(userId.value)
            const hasUnreadMessages = myParticipant?.unreadMessagesCount > 0
            
            try {
                // Fetch messages to get latest message ID
                const messages = await root.$store.dispatch('messaging/fetchMessagesForChannel', { 
                    channelId: channel.value.id, 
                    additional: false 
                })
                
                if (!messages || messages.length === 0) {
                    root.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No messages found to mark as read/unread',
                        life: 3000
                    })
                    return
                }
                
                const latestMessage = messages[0]
                
                if (hasUnreadMessages) {
                    await root.$store.dispatch('messaging/readMessage', { 
                        message: latestMessage 
                    })
                    root.$toast.add({
                        severity: 'success',
                        summary: 'Marked as Read',
                        detail: 'Channel marked as read',
                        life: 3000
                    })
                } else {
                    await root.$store.dispatch('messaging/unreadMessage', { 
                        message: latestMessage 
                    })
                    root.$toast.add({
                        severity: 'info',
                        summary: 'Marked as Unread',
                        detail: 'Channel marked as unread',
                        life: 3000
                    })
                }
            } catch (error) {
                console.error('Error toggling read status:', error)
                root.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update message status',
                    life: 3000
                })
            }
        }

        const watchChannelFromThread = async () => {
            try {
                await root.$store.dispatch('messaging/watchChannel', { 
                    channel: channel.value 
                })
                // Add system message to thread
                addSystemMessage('You are now watching this channel')
                root.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Now watching this channel',
                    life: 3000
                })
            } catch (error) {
                console.error('Error watching channel:', error)
                root.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to watch channel',
                    life: 3000
                })
            }
        }

        const unwatchChannelFromThread = async () => {
            try {
                const message = await promptForMessage('Why are you stopping to watch this channel?')
                await root.$store.dispatch('messaging/unwatchChannel', { 
                    channel: channel.value,
                    message 
                })
                // Add system message to thread
                addSystemMessage(`You stopped watching this channel${message ? ': ' + message : ''}`)
                root.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Stopped watching this channel',
                    life: 3000
                })
            } catch (error) {
                console.error('Error unwatching channel:', error)
                root.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to unwatch channel',
                    life: 3000
                })
            }
        }

        const showInviteDialogFromThread = () => {
            // Emit event to parent to show invite dialog
            emit('showInviteDialog', channel.value)
        }

        const promptForMessage = (title) => {
            return new Promise((resolve) => {
                root.$confirm.require({
                    message: title,
                    header: 'Optional Message',
                    acceptLabel: 'Continue',
                    rejectLabel: 'Cancel',
                    accept: () => resolve(''),
                    reject: () => resolve('')
                })
            })
        }

        const addSystemMessage = (text) => {
            const systemMessage = {
                type: 'system',
                text: text,
                timestamp: new Date().toISOString(),
                id: Date.now() // Unique ID for tracking
            }
            
            // Add to system messages array
            systemMessages.value.unshift(systemMessage)
            
            // Remove after 5 seconds
            setTimeout(() => {
                const index = systemMessages.value.findIndex(item => item.id === systemMessage.id)
                if (index !== -1) {
                    systemMessages.value.splice(index, 1)
                }
            }, 5000)
        }

        onMounted(() => {
            loadChannel()
        })

        onUnmounted(() => {
            if (channelPrincipal.value) unsubscribeFromChannel(channelPrincipal.value)
        })

        watch(channelPrincipal, (oldVal, newVal) => {
            if (oldVal) {
                unsubscribeFromChannel(oldVal)
            }
            loadChannel()
        })

        watch(channels, (updatedChannels) => {
            // Keeps the channel data updated
            if (!channel.value?.id) {
                return
            }
            const updatedChannel = updatedChannels.find((updatedChannel) => updatedChannel.id === channel.value.id)
            if (updatedChannel) {
                channel.value = updatedChannel
            }
        })

        return {
            isMaximized,
            channel,
            profile,
            validatedPhones,
            draftMessage,
            sendingMessage,
            sendAs,
            loading,
            loadingAdditionalMessages,
            systemMessages,
            sendAsOptions,
            fetchError,
            threadItems,
            isManagedByMe,
            isAIChannel,
            patientFullName,
            channelOwnerText,
            hasEarlierMessages,
            primaryPhoneInfo,
            userRoleLabel,
            userRoleBadgeClass,
            phoneTypeBadgeClass,
            participantCount,
            isWatching,
            channelMenuItems,
            channelMenuRef,
            channelMenu_clickHandler,
            toggleReadStatus,
            watchChannelFromThread,
            unwatchChannelFromThread,
            showInviteDialogFromThread,
            promptForMessage,
            addSystemMessage,
            subscribeToChannel,
            unsubscribeFromChannel,
            loadChannel,
            setupInfiniteScrolling,
            loadProfile,
            sendMessage,
            message_submitHandler,
            addDateSeparators,
            shouldStartNewTimePeriod,
        }
    },
}
</script>
<style lang="scss" scoped>
::v-deep .p-tabview {
    .p-tabview-panels {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow: hidden;
        padding: 0;
    }
}

.p-tabview,
.p-tabview-panel {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: auto;
    overflow: hidden;
}

.chat-channel {
    width: 70%;

    .chat-channel-header {
        display: flex;
        align-items: center;
        margin-top: 1px;
        margin-bottom: 13px;
    }
}

.profile-details-avatar-sm {
    width: 28px;
    height: 28px;
    border-radius: 50%;
}

.chat-thread-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
}

.chat-message-thread {
    display: flex;
    flex-direction: column-reverse;
    flex: 1;
    overflow-y: auto;
    padding-right: 4px;
}

.send-error {
    margin: 2px 0;
    padding: 4px 10px;
    border-radius: 12px;
    width: fit-content;
    max-width: 280px;
    background-color: #f18787;
    color: white;
}


.start-message {
    margin: 8px;
    align-self: center;
    color: #6c757d;
    font-size: 13px;
    font-weight: 500;
}

.system-message {
    margin: 6px;
    align-self: center;
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: fadeInOut 5s ease-in-out;
}

@keyframes fadeInOut {
    0% { opacity: 0; transform: translateY(-10px); }
    10% { opacity: 1; transform: translateY(0); }
    90% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-10px); }
}


::-webkit-scrollbar {
    width: 4px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

.show-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
}

.show-scrollbar::-webkit-scrollbar-thumb {
    background: #609af8;
}

.chat-submission {
    padding-top: 12px;
    border-top: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;

    .send-button {
        align-self: flex-end;
        overflow: visible;

        &.disabled {
            opacity: 0.6;
        }

        > :disabled {
            opacity: 0.83;
            color: #fff !important;
        }
    }

    .dropdown-button {
        top: -88px;
    }
}

.owner-enter-active {
    transition: background-color 1s;
}

.owner-leave-active {
    display: none;
}

.owner-enter,
.owner-leave-to {
    background-color: #99ccff;
}
</style>
